import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Car,
  Building2,
  ShieldCheck,
  Sparkles,
  HandHeart,
  Award,
} from "lucide-react";
import {
  BRAND,
  IMAGES,
  SERVICE_AREA,
  DISCLOSURES,
} from "@/config/site";
import { SERVICES } from "@/config/services";
import { useSeo, localBusinessSchema } from "@/lib/seo";
import { MaskedHeading } from "@/components/common/MaskedHeading";
import { BookingButton } from "@/components/common/BookingButton";
import { LinkButton } from "@/components/common/LinkButton";
import { Section, SectionHeading } from "@/components/common/Section";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/common/Reveal";
import { ServiceCard } from "@/components/common/ServiceCard";
import { Marquee } from "@/components/common/Marquee";
import { LocationCard } from "@/components/common/LocationCard";
import { FounderCard } from "@/components/common/FounderCard";
import { CollectiveEndorsement } from "@/components/common/CollectiveEndorsement";
import { CTABand } from "@/components/common/CTABand";

const WHY = [
  {
    icon: HandHeart,
    title: "Client-Centered Care",
    text: "Professional massage therapy focused on how you feel, move and recover — never rushed, always intentional.",
  },
  {
    icon: ShieldCheck,
    title: "Black-Owned & Veteran-Owned",
    text: "A Houston-rooted brand built on service, discipline and a lasting standard of quality.",
  },
  {
    icon: Building2,
    title: "Corporate-Ready",
    text: "On-site chair and table massage designed to fit real workplace schedules and wellness goals.",
  },
  {
    icon: Award,
    title: "A Legacy Forge Collective Company",
    text: "Part of a collective committed to building enduring, high-trust brands.",
  },
];

const CHAPTERS = [
  {
    n: "01",
    title: "Restore",
    text: "Every session begins with intention — creating space to release tension and reset the nervous system.",
  },
  {
    n: "02",
    title: "Recover",
    text: "Therapeutic, professional bodywork that supports how you move, work and live across Greater Houston.",
  },
  {
    n: "03",
    title: "Rejuvenate",
    text: "Whether in-suite, mobile or on-site, wellness is delivered with a premium, welcoming standard.",
  },
];

export default function Home() {
  useSeo({
    title: "Legacy Spa | Massage Therapy & Wellness in Houston",
    description:
      "Legacy Spa offers premium massage therapy and wellness in Greater Houston — in-suite, mobile, and corporate wellness services. Where Wellness Meets Legacy.",
    path: "/",
    jsonLd: localBusinessSchema,
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.82, 0.95]);

  return (
    <>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative isolate flex min-h-[100svh] items-center overflow-hidden bg-legacy-charcoal"
        data-testid="home-hero"
      >
        <motion.div style={{ y: imageY }} className="absolute inset-0 -z-20 scale-110">
          <img
            src={IMAGES.heroBack}
            alt="A calming, professional massage therapy session in a serene treatment space."
            className="h-full w-full object-cover"
          />
        </motion.div>
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 -z-10 bg-gradient-to-t from-legacy-charcoal via-legacy-charcoal/85 to-legacy-charcoal/65"
        />
        <div className="absolute inset-0 -z-10 grain" aria-hidden="true" />

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <CollectiveEndorsement className="mb-8" />
          </motion.div>

          <MaskedHeading
            lines={["Where Wellness", "Meets Legacy."]}
            className="font-heading text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-white leading-[0.92] tracking-tight"
            delay={0.15}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-white/75 leading-relaxed"
          >
            Premium massage therapy and wellness across {SERVICE_AREA} —
            delivered in-suite, at your door, and in the workplace.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <BookingButton size="lg" label="Book an Appointment" testId="hero-book-btn" />
            <LinkButton to="/corporate-wellness" variant="outline" size="lg" testId="hero-corporate-btn">
              Corporate Wellness Consultation
            </LinkButton>
            <LinkButton to="/mobile-massage" variant="outline" size="lg" testId="hero-mobile-btn">
              Mobile Massage
            </LinkButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/60"
          >
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-legacy-mint" aria-hidden="true" /> North Houston Suite
            </span>
            <span className="inline-flex items-center gap-2">
              <Car className="h-4 w-4 text-legacy-mint" aria-hidden="true" /> Mobile across {SERVICE_AREA}
            </span>
            <span className="inline-flex items-center gap-2">
              <Building2 className="h-4 w-4 text-legacy-mint" aria-hidden="true" /> Corporate on-site
            </span>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["Restore", "Recover", "Rejuvenate", "Perform"]} />

      {/* INTRO / POSITIONING */}
      <Section className="bg-legacy-charcoal">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Massage Therapy & Wellness"
              title="A restorative practice, not a beauty salon."
              as="h2"
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                Legacy Spa is a Houston-based massage therapy and wellness
                company serving individuals, mobile clients and organizations.
                Our focus is professional bodywork delivered with a premium,
                welcoming standard — wherever wellness needs to happen.
              </p>
              <p className="mt-5 text-white/55 leading-relaxed">
                As part of Legacy Forge Collective, Legacy Spa is
                Black-owned and veteran-owned, and built to grow with the
                community it serves.
              </p>
              <div className="mt-8">
                <LinkButton to="/about" variant="outline" testId="intro-about-btn">
                  Our Story
                </LinkButton>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* SERVICES OVERVIEW */}
      <Section className="bg-[#12201d]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeading
            eyebrow="What We Offer"
            title="Three ways to experience Legacy Spa."
            as="h2"
          />
          <Reveal delay={0.1}>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-semibold text-legacy-mint hover:text-white transition-colors"
              data-testid="services-viewall-btn"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.slice(0, 3).map((s) => (
            <StaggerItem key={s.id}>
              <ServiceCard service={s} />
            </StaggerItem>
          ))}
        </StaggerGroup>

        <Reveal delay={0.1}>
          <p className="mt-8 text-sm text-white/45">{DISCLOSURES.booking}</p>
        </Reveal>
      </Section>

      {/* MANIFESTO CHAPTERS */}
      <Section className="bg-legacy-charcoal">
        <SectionHeading
          eyebrow="Our Approach"
          title="Wellness, delivered with intention."
          as="h2"
        />
        <div className="mt-14 border-t border-white/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid gap-4 md:grid-cols-12 items-baseline border-b border-white/10 py-10 md:py-14">
                <span className="md:col-span-2 font-heading text-6xl md:text-7xl text-legacy-mint/30">
                  {c.n}
                </span>
                <h3 className="md:col-span-3 font-heading text-3xl md:text-4xl text-white">
                  {c.title}
                </h3>
                <p className="md:col-span-6 md:col-start-7 text-white/65 leading-relaxed">
                  {c.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CORPORATE OVERVIEW (light B2B contrast) */}
      <section className="relative overflow-hidden bg-legacy-ivory py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl">
                <img
                  src={IMAGES.corporateChair}
                  alt="On-site corporate chair massage being provided to an employee at work."
                  className="w-full aspect-[4/3] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <div>
              <SectionHeading
                eyebrow="Corporate Wellness"
                title="Bring restorative massage to your workplace."
                tone="light"
                as="h2"
                lead="On-site chair and table massage, appreciation events, health fairs and recurring programs — built for manufacturing, healthcare, oil & gas, aviation and large employers."
              />
              <div className="mt-8 flex flex-wrap gap-4">
                <LinkButton to="/corporate-wellness" variant="outlineDark" testId="corp-learn-btn">
                  Explore Corporate Wellness
                </LinkButton>
                <LinkButton to="/corporate-wellness#consultation" variant="gold" testId="corp-consult-btn">
                  Request a Consultation
                </LinkButton>
              </div>
              <p className="mt-6 text-sm text-legacy-charcoal/55">
                Confirmed offerings only. Program details are tailored per
                organization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <Section className="bg-legacy-charcoal">
        <SectionHeading
          eyebrow="Why Legacy Spa"
          title="A brand built to last."
          align="center"
          as="h2"
          className="mx-auto"
        />
        <StaggerGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w) => (
            <StaggerItem key={w.title}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-legacy-mint/50">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-legacy-mint/10 text-legacy-mint">
                  <w.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-2xl text-white">{w.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{w.text}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </Section>

      {/* FOUNDER */}
      <Section className="bg-[#12201d]">
        <SectionHeading eyebrow="Leadership" title="Founded on service." as="h2" />
        <div className="mt-14">
          <Reveal>
            <FounderCard />
          </Reveal>
        </div>
      </Section>

      {/* LOCATION */}
      <Section className="bg-legacy-charcoal">
        <SectionHeading
          eyebrow="Our Suite"
          title="Find us in North Houston."
          as="h2"
        />
        <div className="mt-12">
          <Reveal>
            <LocationCard />
          </Reveal>
        </div>
      </Section>

      {/* CTA */}
      <CTABand
        eyebrow="Ready when you are"
        title="Begin your Legacy Spa experience."
        lead="Book an appointment, or start a conversation about mobile and corporate wellness."
        secondary={
          <LinkButton to="/contact" variant="outline" size="lg" testId="cta-contact-btn">
            Contact Us
          </LinkButton>
        }
      />
    </>
  );
}
